import React from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { CalendarDays, Route, User } from "lucide-react";
import { useAskScopeUpdate } from "./useAskScopeUpdate";
import type { ContextualAsk } from "../../chat/AskContext";
import { pageDateFilters } from "../../chat/askScope";
import FilterMultiSelect, { type FilterOption } from "../FilterMultiSelect";
import DashboardDateRange from "../dashboard/DashboardDateRange";

/** Edits affect chat only; exact run selections are narrowed within their page snapshot. */
export default function AskScopeControls({
  target,
  onChange,
  userOptions,
  processOptions,
}: {
  target: ContextualAsk;
  onChange: (target: ContextualAsk) => void;
  userOptions: FilterOption[];
  processOptions: FilterOption[];
}) {
  const { t } = useTranslation();
  const filters = target.filters;
  const isSession = Boolean(filters.recordingIds);
  const pinnedRuns = Boolean(filters.instanceIds) && !isSession;
  const names = filters.processes;
  const options = isSession
    ? (target.sessionProcesses ?? [])
        .filter(name => name.trim())
        .map(name => ({ id: name, label: name }))
    : processOptions.map(option => ({ id: option.label, label: option.label }));
  for (const name of isSession ? [] : (names ?? []).filter(name => name.trim())) {
    if (!options.some(option => option.id === name)) options.push({ id: name, label: name });
  }
  const from =
    target.runSelection?.from ??
    (filters.from && new Date(filters.from).getTime() !== 0
      ? format(new Date(filters.from), "yyyy-MM-dd")
      : "");
  const end = filters.to ? new Date(filters.to) : null;
  // Page ranges have an exclusive midnight end; custom rolling scopes may end mid-day.
  if (end && end.getHours() === 0 && end.getMinutes() === 0 && end.getSeconds() === 0)
    end.setDate(end.getDate() - 1);
  const to = target.runSelection?.to ?? (from && end ? format(end, "yyyy-MM-dd") : "");
  const update = useAskScopeUpdate(target, userOptions, onChange);
  const runControls = pinnedRuns ? (
    <div className="flex w-full min-w-0 flex-wrap items-center gap-1.5 text-xs">
      <span
        className="inline-flex min-w-0 max-w-full items-center gap-1 text-text-mid"
        title={filters.processes?.join(", ")}
      >
        <Route size={14} className="shrink-0" aria-hidden="true" />
        <span className="truncate">{filters.processes?.join(", ")}</span>
      </span>
      {target.runSelection ? (
        <FilterMultiSelect
          pill
          ariaLabel={`${t("chat_change_selection")} (${t("chat_selected_runs", { count: filters.instanceIds!.length })})`}
          value={filters.instanceIds ?? []}
          options={target.runSelection.runs
            .filter(
              run =>
                (!target.contextItems?.some(item => item.kind === "instance") ||
                  target.contextItems.some(
                    item => item.kind === "instance" && item.id === run.id,
                  )) &&
                (!filters.userIds || filters.userIds.includes(run.userId)) &&
                (!from || run.to >= from) &&
                (!to || run.from <= to),
            )
            .map(run => ({ id: run.id, label: run.label }))}
          allLabel={t("chat_selected_runs", { count: filters.instanceIds!.length })}
          emptyLabel={t("chat_selected_runs", { count: 0 })}
          multipleLabel={count => t("chat_selected_runs", { count })}
          selectAllLabel={t("vocabulary_select_all")}
          searchPlaceholder={t("processPage.searchRuns")}
          onChange={selectedIds =>
            update(filters, false, target.datePreset, { ...target.runSelection!, selectedIds })
          }
        />
      ) : (
        <span className="shrink-0 rounded-full border border-outline px-2 py-1 text-text-mid">
          {t("chat_selected_runs", { count: filters.instanceIds!.length })}
        </span>
      )}
    </div>
  ) : null;
  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
      {isSession && (
        <div className="flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
          <span className="inline-flex min-w-0 items-center gap-1 text-text-mid">
            <CalendarDays size={14} className="shrink-0" aria-hidden="true" />
            <span className="min-w-0 break-words">
              <span className="block font-medium text-text">
                {target.sessionLabel ?? target.label}
              </span>
              {target.sessionDetails && <span className="mt-1 block">{target.sessionDetails}</span>}
            </span>
          </span>
          <button
            type="button"
            className="text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
            onClick={() =>
              update(
                {
                  recordingIds: undefined,
                  processes: undefined,
                  userIds: userOptions.map(option => option.id),
                  ...pageDateFilters(),
                },
                true,
              )
            }
          >
            {t("chat_across_sessions")}
          </button>
        </div>
      )}
      {runControls}
      {/* Scope rarely changes but is always on screen, so its pills keep to one row and
          truncate their labels before they wrap. */}
      <div className="flex w-full min-w-0 items-center gap-1">
        {!isSession && (
          <FilterMultiSelect
            pill
            icon={<User size={12} className="shrink-0 text-text-dim" />}
            value={filters.userIds ?? userOptions.map(option => option.id)}
            options={userOptions}
            allLabel={t("watchlist.all_people")}
            selectAllLabel={t("watchlist.all_people")}
            multipleLabel={count => t("dashboard.people_selected", { count })}
            searchPlaceholder={t("search_people")}
            emptyLabel={t("watchlist.select_people")}
            onChange={userIds => update({ ...filters, userIds })}
          />
        )}
        {!pinnedRuns && (
          <FilterMultiSelect
            pill
            icon={<Route size={12} className="shrink-0 text-text-dim" />}
            value={names ?? options.map(option => option.id)}
            options={options}
            allLabel={t(isSession ? "chat_session_processes" : "watchlist.all_processes")}
            selectAllLabel={t(isSession ? "chat_session_processes" : "watchlist.all_processes")}
            multipleLabel={count => t("n_processes", { count })}
            searchPlaceholder={t("search_in_processes")}
            emptyLabel={t("watchlist.select_processes")}
            onChange={processes =>
              update(
                {
                  ...filters,
                  processes:
                    processes.length === options.length && options.length > 0
                      ? undefined
                      : processes,
                  instanceIds: undefined,
                  selectedSteps: undefined,
                },
                true,
              )
            }
          />
        )}
        {!isSession && (!pinnedRuns || target.runSelection) && (
          <div className="min-w-0">
            <DashboardDateRange
              pill
              from={from}
              to={to}
              preset={target.datePreset}
              onChange={(start, finish, preset) =>
                target.runSelection
                  ? update(filters, false, preset, {
                      ...target.runSelection,
                      from: start,
                      to: finish,
                    })
                  : update({ ...filters, ...pageDateFilters(start, finish) }, false, preset)
              }
            />
          </div>
        )}
      </div>
    </div>
  );
}
